import { useState, useEffect, useRef, useCallback } from 'react';
import { string, arrayOf, oneOf, func, shape, number } from 'prop-types';
import classnames from 'classnames';
import Chevron from '../Icon-Chevron/Chevron';
import styles from './Dropdown.module.css';

const ANCHOR_TO_LEFT = 'left';
const ANCHOR_TO_RIGHT = 'right';

const DropDown = ({
  anchor,
  defaultText,
  onDropDownSelect,
  options,
  selectedItemPrefex,
  selectedOption,
}: {
  anchor: string;
  defaultText: string;
  onDropDownSelect: (option: { value: string }) => void;
  options: { label: string; value: string }[];
  selectedItemPrefex: string;
  selectedOption: number;
}) => {
  const [dropDownSelected, setDropDownSelected] = useState(
    selectedOption !== undefined,
  );
  const [dropDownText, setDropDownText] = useState(
    selectedOption !== undefined ? options[selectedOption].label : '',
  );
  const [isClosing, setIsClosing] = useState(false);
  const [isOpen, setIsOpen] = useState(false);

  const dropDownRef = useRef<HTMLDivElement>(null);
  const dropDownBtnRef = useRef<HTMLButtonElement>(null);

  const openDropDown = useCallback(() => {
    setIsOpen(true);
  }, []);

  const closeDropDown = useCallback(() => {
    setIsClosing(true);
    setIsOpen(false);
  }, []);

  const toggleDropDown = useCallback(
    (ev?: React.MouseEvent<HTMLElement>) => {
      if (ev) ev.nativeEvent.stopImmediatePropagation();
      isOpen ? closeDropDown() : openDropDown();
    },
    [closeDropDown, isOpen, openDropDown],
  );

  const selectOption = useCallback(
    (
      { index, text, value }: { index: number; text: string; value: string },
      userSelect = true,
    ) => {
      setDropDownSelected(true);
      setDropDownText(text);

      if (userSelect) {
        onDropDownSelect({ value });
        closeDropDown();
        dropDownBtnRef.current?.focus();
      }
    },
    [closeDropDown, onDropDownSelect],
  );

  const handleOptionSelect = useCallback(
    (ev: React.MouseEvent<HTMLElement>, idx: number, value: string) => {
      const optEl = ev.currentTarget;
      selectOption({
        index: idx,
        text: optEl.innerText,
        value: value,
      });
    },
    [selectOption],
  );
  useEffect(() => {
    if (selectedOption !== undefined) {
      const { label, value } = options[selectedOption];
      selectOption(
        {
          index: selectedOption,
          text: label,
          value,
        },
        false,
      );
    }
  }, [selectedOption]);

  useEffect(() => {
    if (isClosing) setTimeout(() => setIsClosing(false), 300);
  }, [isClosing]);

  useEffect(() => {
    const clickOutOfDropDown = (event: MouseEvent) => {
      console.log(event);
      if (
        event.target instanceof Node &&
        dropDownRef.current &&
        !dropDownRef.current.contains(event.target)
      ) {
        console.log('dropDownRef');
        toggleDropDown();
      }
    };
    if (isOpen) document.addEventListener('click', clickOutOfDropDown);
    return () => document.removeEventListener('click', clickOutOfDropDown);
  }, [isOpen]);

  const rootClx = classnames(styles.dropdown, {
    [styles.isOpen]: isOpen,
    [styles.isClosing]: isClosing,
    [styles.anchoredRight]: anchor !== ANCHOR_TO_LEFT,
    [styles.anchoredLeft]: anchor === ANCHOR_TO_LEFT,
  });

  const ariaSelectedText = `${selectedItemPrefex} ${dropDownText}`;

  return (
    <div className={rootClx} ref={dropDownRef}>
      <div className='overlay' />
      <button
        aria-controls='sort-options'
        aria-expanded={isOpen ? 'true' : 'false'}
        aria-label={`${dropDownSelected ? ariaSelectedText : defaultText}`}
        className={classnames(styles['dropdown__btn'], 'tw-bg-gray-100')}
        id='dropdown-btn'
        onClick={toggleDropDown}
        ref={dropDownBtnRef}
        role='listbox'
        tabIndex={0}
        type='button'
      >
        <span className={styles['dropdown__btn-text-wrapper']}>
          <span className={styles['dropdown__btn-text']}>{`${
            dropDownSelected ? selectedItemPrefex : defaultText
          }`}</span>
          <span
            className={styles['dropdown__btn-selected-text']}
          >{` ${dropDownText}`}</span>
        </span>
        <Chevron toggled={isOpen} />
        <span className={styles['dropdown__btn-symbols']} />
      </button>
      <div className={styles['dropdown__options-wrapper']}>
        <div
          aria-labelledby='dropdown-btn'
          className={classnames(
            styles['dropdown__options-list'],
            'tw-bg-gray-100',
          )}
          id='sort-options'
          role='menu'
        >
          {options.map((item, ndx) => {
            return (
              <button
                aria-hidden={selectedOption === ndx}
                aria-label={item.label}
                className={styles['dropdown__option']}
                data-ndx={ndx}
                disabled={selectedOption === ndx}
                key={item.value}
                onClick={(ev) => handleOptionSelect(ev, ndx, item.value)}
                role='menuitem'
                tabIndex={-1}
                type='button'
              >
                {item.label}
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

DropDown.displayName = 'DropDown';

DropDown.propTypes = {
  anchor: oneOf([ANCHOR_TO_LEFT, ANCHOR_TO_RIGHT]),
  defaultText: string,
  onDropDownSelect: func,
  options: arrayOf(
    shape({
      label: string,
      value: string,
    }),
  ),
  selectedItemPrefex: string,
  selectedOption: number,
};

DropDown.defaultProps = {
  anchor: ANCHOR_TO_LEFT,
};

export default DropDown;
